<template>
  <div class="scaleplus-whats-new">
    <button
      type="button"
      class="scaleplus-whats-new__trigger"
      ref="trigger"
      aria-haspopup="dialog"
      aria-label="What's New"
      :aria-expanded="open ? 'true' : 'false'"
      @click="openDialog"
    >
      <i class="fas fa-bolt" aria-hidden="true"></i>
      <span>What's New</span>
    </button>
    <span v-if="unseenCount" class="scaleplus-whats-new__count">
      {{ unseenCount > 9 ? '9+' : unseenCount }}
    </span>

    <transition name="scaleplus-whats-new-fade">
      <div
        v-if="open"
        class="scaleplus-whats-new__overlay"
        @click.self="closeDialog"
      >
        <section
          class="scaleplus-whats-new__dialog"
          ref="dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="scaleplus-whats-new-title"
        >
          <header class="scaleplus-whats-new__header">
            <div class="scaleplus-whats-new__title-group">
              <span class="scaleplus-whats-new__title-icon">
                <i class="fas fa-bolt" aria-hidden="true"></i>
              </span>
              <h2 id="scaleplus-whats-new-title">What's New</h2>
            </div>
            <div class="scaleplus-whats-new__header-actions">
              <label class="scaleplus-whats-new__filter">
                <span class="sr-only">Filter updates by app</span>
                <select v-model="selectedApplication">
                  <option value="">All apps</option>
                  <option
                    v-for="application in applications"
                    :key="application"
                    :value="application"
                  >
                    {{ application }}
                  </option>
                </select>
              </label>
              <button
                type="button"
                class="scaleplus-whats-new__close"
                ref="close"
                aria-label="Close What's New"
                @click="closeDialog"
              >
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
          </header>

          <div class="scaleplus-whats-new__body">
            <div v-if="loading" class="scaleplus-whats-new__state">
              Loading product updates...
            </div>
            <div v-else-if="error" class="scaleplus-whats-new__state">
              Product updates are temporarily unavailable.
            </div>
            <div v-else-if="!filteredEntries.length" class="scaleplus-whats-new__state">
              No updates yet. Check back soon!
            </div>
            <template v-else>
            <article
              v-for="entry in filteredEntries"
              :key="entry.id"
              class="scaleplus-whats-new__entry"
            >
              <span
                class="scaleplus-whats-new__entry-icon"
                :class="`scaleplus-whats-new__entry-icon--${entryTone(entry.type)}`"
              >
                <i :class="entryIcon(entry.type)" aria-hidden="true"></i>
              </span>
              <div class="scaleplus-whats-new__entry-content">
                <div class="scaleplus-whats-new__entry-heading">
                  <h3>{{ entry.title }}</h3>
                  <span :class="`scaleplus-whats-new__badge--${entryTone(entry.type)}`">
                    {{ entry.typeLabel }}
                  </span>
                </div>

                <img
                  v-if="featureMedia(entry)"
                  class="scaleplus-whats-new__feature"
                  :src="featureMedia(entry).url"
                  :alt="featureMedia(entry).altText || featureMedia(entry).caption || 'Product update screenshot'"
                  loading="lazy"
                />

                <div
                  class="scaleplus-whats-new__description"
                  :class="{ 'scaleplus-whats-new__description--collapsed': canExpand(entry) && !isExpanded(entry.id) }"
                  v-html="entry.descriptionHtml"
                ></div>

                <button
                  v-if="canExpand(entry)"
                  type="button"
                  class="scaleplus-whats-new__expand"
                  :aria-expanded="isExpanded(entry.id) ? 'true' : 'false'"
                  @click="toggleEntry(entry.id)"
                >
                  <i
                    class="fas fa-chevron-down"
                    :class="{ 'scaleplus-whats-new__chevron--open': isExpanded(entry.id) }"
                    aria-hidden="true"
                  ></i>
                  {{ isExpanded(entry.id) ? 'Show less' : 'Read more' }}
                </button>

                <div
                  v-if="isExpanded(entry.id) && galleryMedia(entry).length"
                  class="scaleplus-whats-new__gallery"
                >
                  <figure v-for="media in galleryMedia(entry)" :key="media.id">
                    <img
                      :src="media.url"
                      :alt="media.altText || media.caption || 'Product update screenshot'"
                      loading="lazy"
                    />
                    <figcaption v-if="media.caption">{{ media.caption }}</figcaption>
                  </figure>
                </div>

                <footer class="scaleplus-whats-new__meta">
                  <span>{{ entry.timeAgo }}</span>
                  <span v-if="entry.applicationName">{{ entry.applicationName }}</span>
                </footer>
              </div>
            </article>
            </template>
          </div>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import { renderUpdateMarkdown } from './updateMarkdown';

const SEEN_KEY = 'sp_whats_new_seen';
const FEED_URL = 'https://app.scaleplus.gg/api/app-launcher/whats-new';

export default {
  name: 'ScalePlusWhatsNew',

  props: {
    feedUrl: {
      type: String,
      default: FEED_URL,
    },
  },

  data() {
    return {
      open: false,
      loading: true,
      error: false,
      entries: [],
      seenIds: [],
      selectedApplication: '',
      expandedEntries: {},
      previousBodyOverflow: '',
    };
  },

  computed: {
    unseenCount() {
      return this.entries.filter((entry) => !this.seenIds.includes(String(entry.id))).length;
    },

    applications() {
      const applications = this.entries.reduce((all, entry) => {
        const names = this.entryApplications(entry);
        names.forEach((name) => all.add(name));
        return all;
      }, new Set());

      return Array.from(applications).sort((a, b) => a.localeCompare(b));
    },

    filteredEntries() {
      if (!this.selectedApplication) return this.entries;
      return this.entries.filter((entry) => {
        const names = this.entryApplications(entry);
        return names.includes(this.selectedApplication);
      });
    },
  },

  created() {
    this.readSeenIds();
    this.fetchEntries();
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.unlockBody();
  },

  methods: {
    readSeenIds() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem(SEEN_KEY) || '[]');
        this.seenIds = Array.isArray(parsed) ? parsed.map(String) : [];
      } catch (error) {
        this.seenIds = [];
      }
    },

    async fetchEntries() {
      this.loading = true;
      this.error = false;
      try {
        const response = await window.fetch(this.feedUrl, {
          credentials: 'omit',
          mode: 'cors',
        });
        if (!response.ok) throw new Error(`What's New request failed: ${response.status}`);
        const payload = await response.json();
        this.entries = Array.isArray(payload.entries)
          ? payload.entries.map((entry) => ({ ...entry, descriptionHtml: renderUpdateMarkdown(entry.description) }))
          : [];
      } catch (error) {
        this.error = true;
        this.entries = [];
      } finally {
        this.loading = false;
      }
    },

    openDialog() {
      this.open = true;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.seenIds = this.entries.map((entry) => String(entry.id));
      try {
        window.localStorage.setItem(SEEN_KEY, JSON.stringify(this.seenIds));
      } catch (error) {
        // Seen state is a convenience; the dialog still works without storage.
      }
      this.$nextTick(() => this.$refs.close.focus());
    },

    closeDialog() {
      this.open = false;
      this.unlockBody();
      this.$nextTick(() => this.$refs.trigger.focus());
    },

    unlockBody() {
      if (this.open) this.open = false;
      document.body.style.overflow = this.previousBodyOverflow;
    },

    handleKeydown(event) {
      if (!this.open) return;
      if (event.key === 'Escape') {
        this.closeDialog();
        return;
      }
      if (event.key !== 'Tab' || !this.$refs.dialog) return;

      const focusable = Array.from(this.$refs.dialog.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    entryTone(type) {
      if (type === 'improvement') return 'improvement';
      if (type === 'new_app') return 'new-app';
      if (type === 'bug_fix') return 'bug-fix';
      return 'feature';
    },

    entryApplications(entry) {
      if (Array.isArray(entry.applicationNames)) return entry.applicationNames;
      return entry.applicationName ? [entry.applicationName] : [];
    },

    entryIcon(type) {
      if (type === 'improvement') return 'fas fa-chart-line';
      if (type === 'bug_fix') return 'fas fa-tools';
      if (type === 'new_app') return 'fas fa-bolt';
      return 'fas fa-plus';
    },

    featureMedia(entry) {
      return Array.isArray(entry.media)
        ? entry.media.find((media) => media.placement === 'feature')
        : null;
    },

    galleryMedia(entry) {
      return Array.isArray(entry.media)
        ? entry.media.filter((media) => media.placement !== 'feature')
        : [];
    },

    canExpand(entry) {
      return (entry.description && (entry.description.length > 650 || /\n/.test(entry.description))) || this.galleryMedia(entry).length > 0;
    },

    isExpanded(id) {
      return Boolean(this.expandedEntries[String(id)]);
    },

    toggleEntry(id) {
      const key = String(id);
      this.$set(this.expandedEntries, key, !this.expandedEntries[key]);
    },
  },
};
</script>

<style lang="scss">
.scaleplus-whats-new {
  position: relative;
}

.scaleplus-whats-new__trigger {
  align-items: center;
  background: #785bd8;
  border: 0;
  border-radius: 999px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  gap: 7px;
  height: 38px;
  justify-content: center;
  padding: 0 16px;
  white-space: nowrap;
  transition: box-shadow 160ms ease, transform 160ms ease;

  &:hover,
  &:focus {
    box-shadow: 0 8px 18px rgba(120, 91, 216, 0.26);
    outline: none;
    transform: translateY(-1px);
  }
}

.scaleplus-whats-new__count {
  align-items: center;
  background: #e5484d;
  border: 2px solid #ffffff;
  border-radius: 999px;
  color: #ffffff;
  display: flex;
  font-size: 10px;
  font-weight: 800;
  height: 21px;
  justify-content: center;
  min-width: 21px;
  padding: 0 4px;
  position: absolute;
  right: -8px;
  top: -8px;
}

.scaleplus-whats-new__overlay {
  align-items: center;
  background: rgba(17, 20, 45, 0.48);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 2147482000;
}

.scaleplus-whats-new__dialog {
  background: #ffffff;
  border: 1px solid #dcdcea;
  border-radius: 12px;
  box-shadow: 0 24px 72px rgba(17, 20, 45, 0.24);
  display: flex;
  flex-direction: column;
  height: 86vh;
  max-height: 760px;
  max-width: 680px;
  overflow: hidden;
  width: calc(100vw - 48px);
  white-space: normal;
}

.scaleplus-whats-new__header {
  align-items: center;
  border-bottom: 1px solid #e8eaf1;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 18px 22px;
}

.scaleplus-whats-new__title-group,
.scaleplus-whats-new__header-actions,
.scaleplus-whats-new__entry-heading,
.scaleplus-whats-new__meta {
  align-items: center;
  display: flex;
}

.scaleplus-whats-new__title-group {
  gap: 12px;

  h2 {
    color: #11142d;
    font-size: 18px;
    font-weight: 800;
    line-height: 1.25;
    margin: 0;
  }
}

.scaleplus-whats-new__title-icon {
  align-items: center;
  background: rgba(120, 91, 216, 0.1);
  border-radius: 8px;
  color: #785bd8;
  display: flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.scaleplus-whats-new__header-actions {
  gap: 10px;
}

.scaleplus-whats-new__filter select {
  appearance: none;
  background: #ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 20 20'%3E%3Cpath fill='%235b6680' d='m5 7 5 5 5-5z'/%3E%3C/svg%3E") no-repeat right 12px center;
  border: 1px solid #dcdcea;
  border-radius: 7px;
  color: #11142d;
  font-size: 13px;
  height: 38px;
  max-width: 230px;
  min-width: 170px;
  padding: 0 34px 0 12px;
}

.scaleplus-whats-new__close {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #707991;
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;

  &:hover,
  &:focus {
    background: #f1f3f8;
    color: #11142d;
    outline: none;
  }
}

.scaleplus-whats-new__body {
  overflow-y: auto;
}

.scaleplus-whats-new__state {
  color: #5b6680;
  font-size: 14px;
  padding: 34px 24px;
  text-align: center;
}

.scaleplus-whats-new__entry {
  border-bottom: 1px solid #e8eaf1;
  display: flex;
  gap: 12px;
  padding: 20px 22px;

  &:last-child {
    border-bottom: 0;
  }
}

.scaleplus-whats-new__entry-icon {
  align-items: center;
  border-radius: 8px;
  display: flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
  margin-top: 2px;
}

.scaleplus-whats-new__entry-icon--feature,
.scaleplus-whats-new__badge--feature {
  background: #e8f7ee;
  color: #18794e;
}

.scaleplus-whats-new__entry-icon--improvement,
.scaleplus-whats-new__badge--improvement {
  background: #eee9fb;
  color: #5f42b4;
}

.scaleplus-whats-new__entry-icon--new-app,
.scaleplus-whats-new__badge--new-app {
  background: #fff0f5;
  color: #d92d65;
}

.scaleplus-whats-new__entry-icon--bug-fix,
.scaleplus-whats-new__badge--bug-fix {
  background: #fff3e7;
  color: #a64b00;
}

.scaleplus-whats-new__entry-content {
  min-width: 0;
  width: 100%;
}

.scaleplus-whats-new__entry-heading {
  flex-wrap: wrap;
  gap: 8px;

  h3 {
    color: #11142d;
    font-size: 14px;
    font-weight: 800;
    line-height: 1.5;
    margin: 0;
  }

  span {
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 8px;
  }
}

.scaleplus-whats-new__feature,
.scaleplus-whats-new__gallery img {
  background: #f6f7fa;
  border: 1px solid #e1e4ec;
  border-radius: 8px;
  display: block;
  height: auto;
  margin-top: 12px;
  max-height: 300px;
  object-fit: contain;
  width: 100%;
}

.scaleplus-whats-new__description {
  color: #4f5870;
  font-size: 14px;
  line-height: 1.6;
  margin: 8px 0 0;
  overflow-wrap: anywhere;
}

.scaleplus-whats-new__description--collapsed {
  max-height: 15.5em;
  mask-image: linear-gradient(to bottom, #000 85%, transparent);
  overflow: hidden;
}

.scaleplus-whats-new__description > :first-child { margin-top: 0; }
.scaleplus-whats-new__description > :last-child { margin-bottom: 0; }
.scaleplus-whats-new__description p { margin: 12px 0; }
.scaleplus-whats-new__description h1,
.scaleplus-whats-new__description h2 { margin: 20px 0 10px; font-size: 18px; font-weight: 700; line-height: 1.4; color: #191d3a; }
.scaleplus-whats-new__description h3,
.scaleplus-whats-new__description h4,
.scaleplus-whats-new__description h5,
.scaleplus-whats-new__description h6 { margin: 16px 0 8px; font-size: 15px; font-weight: 700; line-height: 1.4; color: #191d3a; }
.scaleplus-whats-new__description strong { font-weight: 700; }
.scaleplus-whats-new__description em { font-style: italic; }
.scaleplus-whats-new__description ul { list-style: disc; padding-left: 24px; margin: 12px 0; }
.scaleplus-whats-new__description ol { list-style: decimal; padding-left: 24px; margin: 12px 0; }
.scaleplus-whats-new__description li { margin: 4px 0; }
.scaleplus-whats-new__description a { color: #5540b8; text-decoration: underline; }
.scaleplus-whats-new__description blockquote { border-left: 3px solid #c6bedf; padding-left: 14px; margin: 16px 0; }
.scaleplus-whats-new__description hr { border: 0; border-top: 1px solid #e1e4ec; margin: 20px 0; }
.scaleplus-whats-new__description code { background: #f1f3f8; padding: 2px 4px; font-family: monospace; font-size: 13px; }
.scaleplus-whats-new__description pre { max-width: 100%; overflow-x: auto; padding: 12px; margin: 12px 0; background: #f1f3f8; }
.scaleplus-whats-new__description pre code { padding: 0; white-space: pre; overflow-wrap: normal; }
.scaleplus-whats-new__description table { display: block; max-width: 100%; overflow-x: auto; border-collapse: collapse; margin: 12px 0; }
.scaleplus-whats-new__description th,
.scaleplus-whats-new__description td { border: 1px solid #dcdcea; padding: 8px 10px; text-align: left; }
.scaleplus-whats-new__description th { font-weight: 700; background: #f6f7fa; }
.scaleplus-whats-new__description img { max-width: 100%; height: auto; max-height: 384px; object-fit: contain; margin: 12px 0; }

.scaleplus-whats-new__expand {
  align-items: center;
  background: transparent;
  border: 0;
  color: #5b6680;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  margin-top: 8px;
  padding: 0;

  &:hover,
  &:focus {
    color: #11142d;
    outline: none;
  }

  i {
    transition: transform 160ms ease;
  }
}

.scaleplus-whats-new__chevron--open {
  transform: rotate(180deg);
}

.scaleplus-whats-new__gallery {
  display: grid;
  gap: 10px;

  figcaption {
    color: #707991;
    font-size: 12px;
    margin-top: 5px;
  }
}

.scaleplus-whats-new__meta {
  color: #7b849a;
  font-size: 12px;
  gap: 16px;
  margin-top: 12px;

  span:last-child {
    color: #785bd8;
    font-weight: 700;
  }
}

.scaleplus-whats-new-fade-enter-active,
.scaleplus-whats-new-fade-leave-active {
  transition: opacity 160ms ease;
}

.scaleplus-whats-new-fade-enter,
.scaleplus-whats-new-fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .scaleplus-whats-new__trigger {
    height: 36px;
    padding: 0 12px;

    span {
      display: none;
    }
  }

  .scaleplus-whats-new__overlay {
    align-items: flex-end;
    padding: 0;
  }

  .scaleplus-whats-new__dialog {
    border-radius: 12px 12px 0 0;
    max-height: 92vh;
    width: 100vw;
  }

  .scaleplus-whats-new__header {
    align-items: flex-start;
    padding: 16px;
  }

  .scaleplus-whats-new__header-actions {
    align-items: flex-end;
    flex-direction: column-reverse;
  }

  .scaleplus-whats-new__filter select {
    min-width: 145px;
    width: 145px;
  }

  .scaleplus-whats-new__entry {
    padding: 18px 16px;
  }
}
</style>
